import { api, apiBlob, apiUpload } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type {
  AttritionDocumentReviewStatus,
  AttritionRequestDetail,
  AttritionRequestListItem,
  AttritionRequestListQuery,
  AttritionRequestRequest,
} from './attritionTypes';

function toQueryString(query: AttritionRequestListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Each facet is sent whenever its key is present, empty string included — "ticked to nothing"
  // matches no row, where an absent key is no constraint at all.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.category !== undefined) params.set('category', query.category);
  if (query.awaitingMe) params.set('awaitingMe', 'true');

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/attrition-requests';

export const attritionApi = {
  list: (query: AttritionRequestListQuery, signal?: AbortSignal) =>
    api<PagedResult<AttritionRequestListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<AttritionRequestDetail>(`${BASE}/${id}`, { signal }),

  create: (body: AttritionRequestRequest) => api<AttritionRequestDetail>(BASE, { method: 'POST', body }),

  update: (id: string, body: AttritionRequestRequest) =>
    api<AttritionRequestDetail>(`${BASE}/${id}`, { method: 'PUT', body }),

  /**
   * Files the submitted document for one checklist line, or replaces one not yet approved.
   * Through `apiUpload` — multipart, real progress.
   */
  uploadDocument: (id: string, lineId: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<AttritionRequestDetail>(`${BASE}/${id}/documents/${lineId}`, form, { onProgress });
  },

  /**
   * Fetches one submitted document. A FETCH then an object URL, never an `<a href>` at the
   * endpoint: the token travels in a header no navigation sends.
   */
  downloadDocument: (id: string, lineId: string, signal?: AbortSignal) =>
    apiBlob(`${BASE}/${id}/documents/${lineId}/file`, { signal }),

  /** The line's own reviewer approves it, or returns it with a reason. */
  reviewDocument: (id: string, lineId: string, body: { decision: AttritionDocumentReviewStatus; remarks: string | null }) =>
    api<AttritionRequestDetail>(`${BASE}/${id}/documents/${lineId}/review`, { method: 'POST', body }),

  cancel: (id: string, reason: string) =>
    api<AttritionRequestDetail>(`${BASE}/${id}/cancel`, { method: 'POST', body: { reason } }),

  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),
};
